/**
 * HTTP Utilities - Centralized functions for HTTP requests
 *
 * RULES:
 * 1. ALWAYS use handleResponse() for processing wrapped API responses
 * 2. ALWAYS use handleDirectResponse() for direct response data
 * 3. NEVER duplicate this logic in other files
 * 4. Use the types APIResponse and APIError defined here
 */

import { firebaseClient } from '../services/firebase/firebase-client';
import { getHttpErrorMessage } from '../constants/errorMessages';
import { API_ERROR_CODES, HTTP_STATUS } from '../constants/apiErrorCodes';
import { isE2ETestMode } from './apiMode';
import type { ApiResponse } from '../types';

/**
 * Standard API response wrapper
 */
export interface APIResponse<T> {
  success: boolean;
  data?: T;
  error?: string;
  message?: string;
}

/**
 * Standard API error response
 */
export interface APIError {
  code?: string;
  message?: string;
  error?: string;
  details?: unknown;
  status?: number;
}

/**
 * Error thrown by handleResponse()/handleDirectResponse() for a failed request.
 *
 * Extends Error, so every existing `err instanceof Error ? err.message : ...`
 * caller keeps working unchanged. The addition is `status` and `code`: without
 * them a component can only render the backend's prose, which is how a raw
 * server diagnostic ends up on screen. With them, a component can branch on the
 * CONDITION (401 -> session expired) and show its own translated message.
 */
export class HttpResponseError extends Error {
  /** HTTP status of the failed response. */
  readonly status: number;
  /** Envelope `error.code` when the backend sent one (e.g. "UNAUTHORIZED"). */
  readonly code?: string;

  constructor(message: string, status: number, code?: string) {
    super(message);
    this.name = 'HttpResponseError';
    this.status = status;
    this.code = code;
  }
}

/**
 * Read the machine-readable `error.code` out of a backend error body.
 *
 * Accepts both envelope shapes the API emits: `{error: {code}}` (error_response)
 * and a bare `{code}` / `{error_code}`.
 */
export function extractResponseErrorCode(errorData: Record<string, unknown>): string | undefined {
  const error = errorData.error;
  if (error && typeof error === 'object' && !Array.isArray(error)) {
    const code = (error as Record<string, unknown>).code;
    if (typeof code === 'string') return code;
  }
  if (typeof errorData.code === 'string') return errorData.code;
  if (typeof errorData.error_code === 'string') return errorData.error_code;
  return undefined;
}

/**
 * Extract error message from API error response body.
 * Handles multiple backend error formats:
 * 1. FastAPI HTTPException: {detail: "string"} or {detail: {message: "string"}}
 * 2. FastAPI 422 validation: {detail: [{msg: "string", loc: [...]}]}
 * 3. Generic exception handler: {error: {code, message, details}}
 * 4. Direct format: {message: "string"} or {error: "string"}
 *
 * Exported because services that cannot use handleResponse() - e.g. XMLHttpRequest
 * uploads, which need progress events - must still get the SAME message extraction
 * instead of hand-rolling `body.error || body.detail`, which renders the envelope's
 * error OBJECT as "[object Object]".
 */
export function extractResponseErrorMessage(
  errorData: Record<string, unknown>
): string | undefined {
  // FastAPI HTTPException wraps errors under 'detail'
  const detail = errorData.detail;
  if (typeof detail === 'string') {
    return detail;
  }
  if (detail && typeof detail === 'object') {
    // FastAPI 422 validation errors: detail is an array of {loc, msg, type}
    if (Array.isArray(detail) && detail.length > 0) {
      const first = detail[0] as Record<string, unknown>;
      if (typeof first.msg === 'string') {
        const loc = Array.isArray(first.loc) ? first.loc.join('.') : '';
        return loc ? `${loc}: ${first.msg}` : first.msg;
      }
    }
    // HTTPException with dict detail: {code, message, details}
    if (!Array.isArray(detail)) {
      const d = detail as Record<string, unknown>;
      if (typeof d.message === 'string') return d.message;
      if (typeof d.error === 'string') return d.error;
      // Structured validation payload, e.g. {validation_errors: [...]} from the
      // YouTube publish route. Join into a readable sentence so the raw dict is
      // never stringified into the UI.
      const detailList = d.validation_errors ?? d.errors;
      if (Array.isArray(detailList) && detailList.length > 0) {
        return detailList.map((e) => String(e)).join('; ');
      }
    }
  }
  // Direct format
  if (typeof errorData.message === 'string') return errorData.message;
  // Top-level structured validation payload (no 'detail' wrapper).
  const topLevelList = errorData.validation_errors ?? errorData.errors;
  if (Array.isArray(topLevelList) && topLevelList.length > 0) {
    return topLevelList.map((e) => String(e)).join('; ');
  }
  // Error field: can be string or object {code, message, details}
  const error = errorData.error;
  if (typeof error === 'string') return error;
  if (error && typeof error === 'object' && !Array.isArray(error)) {
    const e = error as Record<string, unknown>;
    if (typeof e.message === 'string') return e.message;
  }
  return undefined;
}

/**
 * Process HTTP response with success/data wrapper
 * Use for APIs that return { success: boolean, data: T, error?: string }
 * @throws Error if response is not OK or if API returns error
 */
export async function handleResponse<T>(response: Response): Promise<T> {
  if (!response.ok) {
    let errorMessage = getHttpErrorMessage(response.status);
    let errorCode: string | undefined;
    try {
      const errorData = await response.json();
      errorMessage = extractResponseErrorMessage(errorData) || errorMessage;
      errorCode = extractResponseErrorCode(errorData);
    } catch {
      // Response is not valid JSON, use default message
    }
    // Surface every API failure in the app console (the user reported errors
    // that never appeared in the logs). Kept here so all callers benefit.
    console.error(`[API] ${response.status} ${response.url}: ${errorMessage}`);
    throw new HttpResponseError(errorMessage, response.status, errorCode);
  }

  const data = (await response.json()) as APIResponse<T>;

  if (!data.success) {
    const errorMsg =
      typeof data.error === 'string'
        ? data.error
        : (data.message ?? 'Request failed (success=false)');
    throw new Error(errorMsg);
  }

  return data.data as T;
}

/**
 * Process HTTP response that returns data directly (no wrapper)
 * Use for APIs that return the object directly without success/data wrapper
 * @throws Error if response is not OK
 */
export async function handleDirectResponse<T>(response: Response): Promise<T> {
  if (!response.ok) {
    let errorMessage = getHttpErrorMessage(response.status);
    let errorCode: string | undefined;
    try {
      const errorData = await response.json();
      errorMessage = extractResponseErrorMessage(errorData) || errorMessage;
      errorCode = extractResponseErrorCode(errorData);
    } catch {
      // Response is not valid JSON, use default message
    }
    // Surface every API failure in the app console (the user reported errors
    // that never appeared in the logs). Kept here so all callers benefit.
    console.error(`[API] ${response.status} ${response.url}: ${errorMessage}`);
    throw new HttpResponseError(errorMessage, response.status, errorCode);
  }

  return (await response.json()) as T;
}

/**
 * Unwrap the canonical backend {success: boolean, data: T} envelope.
 *
 * RULE #-2: ONE envelope. Every shared-route endpoint that uses success_response()
 * returns this shape. Call this helper instead of writing `body.data ?? body`
 * inline — the fallback silently swallows wrong shapes.
 *
 * Throws a descriptive error when:
 *   - `body` is not an object
 *   - `success` or `data` keys are missing
 *   - `success` is false
 *
 * Only call this for endpoints CONFIRMED to use success_response(). For bare-
 * returning endpoints (e.g. ltx2 routes, component path endpoints) use the
 * body directly.
 *
 * @param body - The parsed JSON response body (the full envelope)
 * @returns The inner `data` payload cast to T
 * @throws Error if the envelope contract is violated
 */
export function unwrapEnvelope<T>(body: unknown): T {
  if (body === null || body === undefined || typeof body !== 'object') {
    throw new Error(
      `[unwrapEnvelope] Expected {success, data} envelope but received: ${JSON.stringify(body)}`
    );
  }
  const obj = body as Record<string, unknown>;
  if (!('success' in obj) || !('data' in obj)) {
    throw new Error(
      `[unwrapEnvelope] Response missing required envelope fields (success, data). Got: ${JSON.stringify(Object.keys(obj))}`
    );
  }
  if (obj.success !== true) {
    const err = obj.error;
    const errMsg =
      typeof err === 'string'
        ? err
        : err !== null && err !== undefined && typeof err === 'object'
          ? String((err as Record<string, unknown>).message ?? JSON.stringify(err))
          : String(obj.message ?? 'Request failed (success=false)');
    throw new Error(`[unwrapEnvelope] API returned success=false: ${errMsg}`);
  }
  return obj.data as T;
}

/**
 * Extract error message from unknown error safely
 * @param error - The caught error (unknown type)
 * @param defaultMessage - Fallback message if error cannot be extracted
 * @returns Human-readable error message
 */
export function extractErrorMessage(error: unknown, defaultMessage = 'Network error'): string {
  // Axios errors carry the real backend error envelope in error.response.data,
  // but error.message is always axios's own generic "Request failed with
  // status code N" (set in its settle.js, regardless of the response body).
  // Check the response body FIRST — every apiClient-based feature (motion
  // control, image editing, scene generation, talking heads) throws a raw
  // AxiosError on a non-2xx submit call, and without this, the user only ever
  // sees "Request failed with status code 400" instead of the sanitized
  // backend message.
  if (typeof error === 'object' && error !== null && 'response' in error) {
    const axiosLike = error as { response?: { data?: unknown } };
    if (axiosLike.response?.data && typeof axiosLike.response.data === 'object') {
      const fromBody = extractResponseErrorMessage(
        axiosLike.response.data as Record<string, unknown>
      );
      if (fromBody) {
        return fromBody;
      }
    }
  }
  if (error instanceof Error) {
    return error.message;
  }
  if (typeof error === 'string') {
    return error;
  }
  // Handle API error object format: {code, message, details}
  if (typeof error === 'object' && error !== null) {
    const errObj = error as Record<string, unknown>;
    if (typeof errObj.message === 'string') {
      return errObj.message;
    }
    if (typeof errObj.error === 'string') {
      return errObj.error;
    }
    // Nested error object: error: {code, message}
    if (typeof errObj.error === 'object' && errObj.error !== null) {
      const nested = errObj.error as Record<string, unknown>;
      if (typeof nested.message === 'string') {
        return nested.message;
      }
    }
  }
  return defaultMessage;
}

/**
 * Extract error message from API response data
 * Handles both string errors and object format: {code, message, details}
 * @param data - Response data object
 * @param defaultMessage - Fallback message
 * @returns Human-readable error message
 */
export function extractApiErrorMessage(
  data: Record<string, unknown>,
  defaultMessage = 'Request failed'
): string {
  // Try direct message first
  if (typeof data.message === 'string') {
    return data.message;
  }

  // Handle error field (can be string or object)
  const error = data.error;
  if (typeof error === 'string') {
    return error;
  }
  if (typeof error === 'object' && error !== null) {
    const errObj = error as Record<string, unknown>;
    if (typeof errObj.message === 'string') {
      return errObj.message;
    }
    if (typeof errObj.code === 'string') {
      return errObj.code;
    }
  }

  return defaultMessage;
}

/**
 * Standard headers for JSON requests with a BODY (POST, PUT, DELETE).
 *
 * WARNING: Do NOT use on GET requests! Content-Type: application/json on a
 * bodyless GET triggers a CORS preflight OPTIONS request, which can fail on
 * cold Cloud Run instances causing intermittent "CORS error" with status null.
 * GET requests without custom headers are "simple requests" that skip preflight.
 *
 * X-Requested-With is required by the backend CSRF protection middleware.
 * Browsers cannot forge this header in cross-origin requests without triggering
 * a CORS preflight, which the backend rejects for unknown origins.
 *
 * E2E test mode (when `isE2ETestMode()` returns true at module load) injects
 * `X-E2E-Test: true` so the serverless auth middleware (which gates on the
 * `_E2E_TEST_MODE=true` env var AND this header) bypasses Firebase auth.
 * The conditional is evaluated ONCE at module-eval time; toggling E2E mode
 * mid-session will NOT update headers — fine for Playwright runs where the
 * flag is set before the FE bundle loads.
 */
export const JSON_HEADERS: Record<string, string> = {
  'Content-Type': 'application/json',
  'X-Requested-With': 'XMLHttpRequest',
  ...(isE2ETestMode() ? { 'X-E2E-Test': 'true' } : {}),
};

/**
 * Create headers with optional authorization token
 * @param token - Optional auth token (Bearer format)
 * @returns HeadersInit with Content-Type and optional Authorization
 */
export function getAuthHeaders(token?: string | null): HeadersInit {
  const headers: Record<string, string> = { ...JSON_HEADERS };
  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }
  return headers;
}

/**
 * Create headers with a fresh Firebase ID token.
 * Uses firebaseClient.getIdToken() which auto-refreshes expired tokens.
 * @returns Promise<HeadersInit> with Content-Type and Authorization
 */
export async function getAuthHeadersFromStorage(): Promise<HeadersInit> {
  const token = await firebaseClient.getIdToken();
  return getAuthHeaders(token);
}

/**
 * Perform a fetch and resolve to a result-style ApiResponse<T> — never throws.
 *
 * Consolidates the `fetch -> json -> (!ok ? error : unwrap) -> catch` boilerplate
 * repeated across the result-style API services (image / music / carousel). On a
 * non-OK status the message is extracted with extractApiErrorMessage; on a thrown
 * error (network failure or invalid JSON) it falls back to extractErrorMessage.
 * Both use `fallbackMsg` as the last resort. On success the response envelope is
 * unwrapped (`data.data` when present, else the body itself), matching the
 * hand-rolled services it replaces.
 *
 * Use this for endpoints that return `{ success, data, error }` and whose callers
 * branch on `result.success`. For endpoints that should THROW on failure, use
 * handleResponse() / handleDirectResponse() instead.
 */
export async function requestWrapped<T>(
  url: string,
  init: RequestInit,
  fallbackMsg = 'Request failed'
): Promise<ApiResponse<T>> {
  try {
    const response = await fetch(url, init);
    const data = await response.json();

    if (!response.ok) {
      // `errorCode` and `status` are what make the failure ACTIONABLE. Without
      // them every caller had one string to work with and had to render the
      // backend's prose verbatim — which is how a customer who simply ran out
      // of tokens was shown "Set it in Settings > API Keys" instead of an offer
      // to buy more. `ApiResponse.errorCode` has existed (and been read by
      // services/polling/imagePollAdapter.ts) since long before this function
      // was written; this is the producer that was never filling it in.
      return {
        success: false,
        error: extractApiErrorMessage(data, fallbackMsg),
        errorCode: extractResponseErrorCode(data as Record<string, unknown>),
        status: response.status,
      };
    }

    return { success: true, data: unwrapEnvelope<T>(data) };
  } catch (error) {
    return { success: false, error: extractErrorMessage(error, fallbackMsg) };
  }
}

/**
 * Shape shared by the two ways a failure reaches a component: the result-style
 * `ApiResponse` (from `requestWrapped`) and the thrown `HttpResponseError`
 * (from `handleResponse` / `handleDirectResponse`).
 */
export interface ApiFailureSignals {
  errorCode?: string;
  status?: number;
}

/**
 * True when the backend refused because the signed-in account cannot pay.
 *
 * Keyed on the envelope code first and the HTTP status second — the two agree
 * wherever the error catalog is used, and the status alone still catches the
 * routes that answer 402 with no code (`serverless/api/routes/tokens.py` raises
 * a bare HTTPException with the code nested under `detail`).
 *
 * This is deliberately ONE function rather than an `=== 402` in each caller:
 * every surface that can be refused for money must reach the same conclusion,
 * and a per-component copy is how one of them ends up showing "try again later"
 * for a refusal that will never resolve on its own.
 */
export function isInsufficientTokensFailure(failure: ApiFailureSignals): boolean {
  return (
    failure.errorCode === API_ERROR_CODES.INSUFFICIENT_TOKENS ||
    failure.status === HTTP_STATUS.PAYMENT_REQUIRED
  );
}

/**
 * True when the backend refused because no usable credential was presented.
 *
 * Includes the desktop billing path: off-cloud work with a real cloud cost and
 * no forwarded Firebase token raises `BillingAuthenticationRequiredError`, which
 * the error handler maps to 401 UNAUTHORIZED. The remedy is signing in, not
 * retrying.
 */
export function isAuthRequiredFailure(failure: ApiFailureSignals): boolean {
  return (
    failure.errorCode === API_ERROR_CODES.UNAUTHORIZED ||
    failure.errorCode === API_ERROR_CODES.INVALID_TOKEN ||
    failure.errorCode === API_ERROR_CODES.TOKEN_EXPIRED ||
    failure.status === HTTP_STATUS.UNAUTHORIZED
  );
}

/**
 * Turn a failed `ApiResponse` into a THROWABLE error that keeps its signals.
 *
 * For call sites that must throw (e.g. the `ImageGenRunner` contract, whose
 * `start()` reports failure by throwing): `throw new Error(resp.error)` erases
 * the code and status that `requestWrapped` just recovered, leaving whoever
 * catches it with prose again. Throw this instead.
 */
export function apiFailureError(
  response: ApiResponse<unknown>,
  fallbackMsg: string
): HttpResponseError {
  return new HttpResponseError(
    response.error ?? fallbackMsg,
    response.status ?? 0,
    response.errorCode
  );
}
